import React, { useEffect, useState } from 'react'
import { getDashboardUserSessionReports } from '../../../../../store/dashboard/actions'
import { SessionsCount } from '../../../../../store/dashboard/reducer'
import * as htmlToImage from 'html-to-image'
import download from 'downloadjs'
import { useTranslation } from 'react-i18next'
import UsageTrendsChartView from './UsageTrendsChartView'
import { useQuery } from '@tanstack/react-query'
import { getAgentFromStorage } from '@/utils/appUtils'

interface UsageTrendsChartProps {
  filtersData?: any
  isOrganisation?: boolean
}

const UsageTrendsChart = ({
  filtersData: filtersDataProps,
  isOrganisation,
}: UsageTrendsChartProps) => {
  const { t } = useTranslation('translation')
  const [chartDownloading, setChartDownloading] = useState(false)
  const [filtersData, setFiltersData] = useState({
    type: 'daily',
    filter_from_date: null,
    filter_to_date: null,
  })
  const [frequency, setFrequency] = useState({
    value: 'daily',
    label: t('Daily'),
  })
  const [dateParameters, setDateParameters] = useState({
    value: '30',
    label: t('Last 30 days'),
  })

  const agentId = getAgentFromStorage()

  const { isLoading, data } = useQuery({
    queryKey: [
      isOrganisation ? 'organisation-usage-trends' : 'usage-trends',
      filtersData,
      isOrganisation,
      agentId,
    ],
    enabled: !!(filtersData.filter_from_date && filtersData.filter_to_date),
    queryFn: async () => {
      const data = await getDashboardUserSessionReports({
        filtersData,
        isOrganisation,
      })
      const barChartData: SessionsCount[] = [
        ...data.activeUsersSessions.map((d: any) => {
          return {
            ...d,
            type: 'usageTrends',
          }
        }),
      ]
      const key1 = 'session_count',
        key2 = 'user_count'

      const result: number = Math.max.apply(
        Math,
        barChartData.map(function (a) {
          return Math.max(a[key1], a[key2])
        }),
      )

      return { maxValue: result, sessionsData: barChartData }
    },
  })

  useEffect(() => {
    if (
      !filtersDataProps.filter_from_date ||
      !filtersDataProps.filter_to_date
    ) {
      return
    }

    if (filtersDataProps.dateParameters) {
      setDateParameters(filtersDataProps.dateParameters)
    }

    setFiltersData(prev => ({
      ...prev,
      ...filtersDataProps,
    }))
  }, [filtersDataProps])

  useEffect(() => {
    if (dateParameters.value !== 'custom') {
      setFiltersData(prev => ({
        ...prev,
        type: frequency.value,
      }))
    }
  }, [frequency.value, dateParameters.value])

  const handleFrequencyChange = (option: any) => {
    setFrequency(option)
    if (dateParameters.value === 'custom') {
      setFiltersData({
        ...filtersData,
        type: option?.value,
      })
    }
  }

  const handleDownloadChart = async () => {
    setChartDownloading(true)
    htmlToImage
      .toJpeg(document.getElementById('usage_trends'))
      .then(async function (dataUrl) {
        await download(dataUrl, 'active-users-sessions.png')
        setChartDownloading(false)
      })
      .catch(async (e: any) => {
        setChartDownloading(false)
        console.log('Error', e)
      })
  }

  return (
    <UsageTrendsChartView
      loading={isLoading}
      chartDownloading={chartDownloading}
      sessionsData={data?.sessionsData || []}
      filtersData={filtersData}
      frequency={frequency}
      maxValue={data?.maxValue || 0}
      onFrequencyChange={handleFrequencyChange}
      onDownloadChart={handleDownloadChart}
    />
  )
}

export default UsageTrendsChart
